'use client';
import { useEffect, useState } from 'react';

type Health = {
  ok: boolean;
  engine: string;
  message?: string;
  last_ok?: string | null;
  last_error?: { at: string | null; message?: string | null };
};

type Guard = {
  warning: boolean;
  blocked: boolean;
  writesInWindow: number;
  burstsLastHour: number;
  thresholdPerWindow: number;
  windowMs: number;
  maxBurstsPerHour: number;
};

export default function ConfigDbBanner() {
  const [health, setHealth] = useState<Health | null>(null);
  const [guard, setGuard] = useState<Guard | null>(null);

  async function load(signal?: AbortSignal) {
    try {
      const res = await fetch('/api/admin/db-status', { cache: 'no-store', signal });
      if (res.status === 401 || res.status === 403) {
        setHealth(null);
        setGuard(null);
        return;
      }
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setHealth(data.health);
      setGuard(data.sqliteGuard ?? null);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.warn('DB status check failed:', err instanceof Error ? err.message : err);
      setHealth({ ok: false, engine: 'unknown', message: 'Unable to check database status' });
      setGuard(null);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const id = setInterval(() => load(controller.signal), 5 * 60 * 1000);
    return () => {
      clearInterval(id);
      controller.abort();
    };
  }, []);

  // Config DB hard-fail
  if (health && !health.ok) {
    return (
      <div style={{ background: '#7f1d1d', color: 'white', padding: '10px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
        <strong>Config database unreachable.</strong>
        <span>{health.message || 'Please check the database connection.'}</span>
        <a href="/admin/site-settings?tab=database" style={{ color: '#bfdbfe', textDecoration: 'underline' }}>Open Database Settings</a>
      </div>
    );
  }

  // SQLite guard warnings (only for sqlite engine)
  const isSqlite = health?.engine === 'sqlite';
  const guardActive = isSqlite && guard && (guard.blocked || guard.warning || guard.burstsLastHour > 0);
  if (guardActive && guard) {
    const bg = guard.blocked ? '#7f1d1d' : '#92400e';
    const textColor = '#fff';
    const linkColor = '#fef3c7';
    const burstsInfo = `${guard.burstsLastHour}/${guard.maxBurstsPerHour + 1} bursts in last hour`;
    const writesInfo = `Writes this minute: ${guard.writesInWindow}/${guard.thresholdPerWindow}`;
    const message = guard.blocked
      ? 'High SQLite write load detected; submissions are temporarily paused to protect the database.'
      : 'High SQLite write volume detected; submissions remain enabled, but you may experience throttling.';

    return (
      <div style={{ background: bg, color: textColor, padding: '10px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>SQLite performance warning.</strong>
        <span>{message}</span>
        <span style={{ opacity: 0.9 }}>{burstsInfo} · {writesInfo}</span>
        <a href="/admin/site-settings?tab=database" style={{ color: linkColor, textDecoration: 'underline' }}>Switch to external DB</a>
      </div>
    );
  }

  return null;
}
